import React, { useState } from 'react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TuneIcon from '@mui/icons-material/Tune';
import { colors, typography } from '../../../styles/theme';
import { FILTER_ACCENT, FILTER_ACCENT_14 } from '../FilterPanel/FilterPanel.styles';
import { buildParsedSearchDisplay, truncateNlEcho } from '../../utils/nlSearchQuery';
import { NL_SEARCH_INTENTS, NL_SEARCH_QUOTA_EXCEEDED_CODE } from '../../data/nlSearchConstants';
import SearchIdleSuggestions from '../PlaceSearchResults/SearchIdleSuggestions';

function formatChipLabel(label, approximate) {
  if (!approximate) return label;
  const idx = String(label).indexOf(': ');
  if (idx >= 0) {
    return `${label.slice(0, idx + 2)}~ ${label.slice(idx + 2)}`;
  }
  return `~ ${label}`;
}

/**
 * Resumen de lo que el parse entendió, en el dropdown del buscador.
 * Sin título de intent: los chips (lugar / filtros) alcanzan.
 *
 * @param {{
 *   data?: object,
 *   error?: string|null,
 *   errorCode?: string|null,
 *   loading?: boolean,
 *   isMock?: boolean,
 *   mapOptionsByField?: object,
 *   onOpenFilters?: (groupId: string) => void,
 *   suggestedQueries?: string[],
 *   onSelectQuery?: (query: string) => void,
 * }} props
 */
function ParsedSearchSummary({
  data,
  error,
  errorCode = null,
  loading,
  isMock = false,
  mapOptionsByField = null,
  onOpenFilters = null,
  suggestedQueries = [],
  onSelectQuery,
}) {
  if (loading) {
    return (
      <div style={styles.loading} role="status" aria-live="polite">
        <span>Estamos analizando tu búsqueda</span>
        <span className="nl-search-thinking-dots" aria-hidden>
          <span style={styles.dot} />
          <span style={styles.dot} />
          <span style={styles.dot} />
        </span>
      </div>
    );
  }

  if (error) {
    const showQuotaFiltersLink = errorCode === NL_SEARCH_QUOTA_EXCEEDED_CODE
      && typeof onOpenFilters === 'function';
    return (
      <div style={styles.statusError}>
        <div>{error}</div>
        {showQuotaFiltersLink && (
          <button
            type="button"
            style={styles.statusErrorAction}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onOpenFilters(null)}
          >
            Usar Filtros
          </button>
        )}
      </div>
    );
  }

  if (!data) return null;

  const display = buildParsedSearchDisplay(data, mapOptionsByField);
  const unresolved = Array.isArray(data.unresolved) ? data.unresolved.filter(Boolean) : [];
  const showLowConfidence = typeof data.confidence === 'number' && data.confidence < 0.5;
  const isUnknown = data.intent === NL_SEARCH_INTENTS.unknown;
  const hasApprox = display.some((item) => item.approximate);
  const canOpenFilters = typeof onOpenFilters === 'function';
  const adjustGroupId = display.find((item) => item.approximate)?.groupId
    || display.find((item) => item.field)?.groupId
    || 'dimensiones';
  const showAdjustLink = canOpenFilters && !isUnknown && (hasApprox || unresolved.length > 0);

  return (
    <div style={styles.wrap}>
      {(isUnknown || isMock) && (
        <div style={{
          ...styles.header,
          justifyContent: isUnknown ? 'space-between' : 'flex-end',
        }}
        >
          {isUnknown && (
            <span style={styles.headerLabel}>No encontramos coincidencias</span>
          )}
          {isMock && <span style={styles.mockBadge}>Mock</span>}
        </div>
      )}

      {showLowConfidence && (
        <div style={styles.note}>Interpretación poco segura. Revisá los filtros antes de fiarte.</div>
      )}

      {display.length > 0 && (
        <ul style={styles.list}>
          {display.map((item, index) => (
            <ResultChip
              key={`${item.kind || item.field}-${item.label}`}
              item={item}
              index={index}
              onOpenFilters={onOpenFilters}
            />
          ))}
        </ul>
      )}

      {unresolved.length > 0 && (
        <div style={styles.unresolved}>
          <div style={styles.unresolvedHeader}>
            <div style={styles.unresolvedTitle}>No pude usar</div>
            {showAdjustLink && (
              <button
                type="button"
                style={styles.adjustLink}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onOpenFilters(adjustGroupId)}
              >
                Ajustar en Filtros
              </button>
            )}
          </div>
          <ul style={styles.unresolvedList}>
            {unresolved.map((item) => {
              const text = truncateNlEcho(item);
              return (
                <li key={item} title={String(item)}>
                  {text}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {showAdjustLink && unresolved.length === 0 && (
        <div style={styles.adjustRow}>
          <button
            type="button"
            style={styles.adjustLink}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onOpenFilters(adjustGroupId)}
          >
            Ajustar en Filtros
          </button>
        </div>
      )}

      {isUnknown && suggestedQueries.length > 0 && (
        <SearchIdleSuggestions
          suggestions={suggestedQueries}
          recentQueries={[]}
          onSelectQuery={onSelectQuery}
        />
      )}
    </div>
  );
}

function ResultChip({ item, index, onOpenFilters }) {
  const [hovered, setHovered] = useState(false);
  const clickable = typeof onOpenFilters === 'function' && item.groupId;
  const approximate = Boolean(item.approximate);
  const label = formatChipLabel(item.label, approximate);

  const content = (
    <>
      {item.kind === 'location'
        ? <LocationOnIcon style={styles.icon} />
        : <TuneIcon style={{ ...styles.icon, opacity: approximate ? 0.7 : 1 }} />}
      <span style={styles.itemLabel}>{label}</span>
    </>
  );

  if (!clickable) {
    return (
      <li
        className="nl-search-result-chip"
        style={{
          ...styles.item,
          animationDelay: `${index * 60}ms`,
          ...(approximate ? styles.itemApprox : {}),
        }}
      >
        {content}
      </li>
    );
  }

  return (
    <li style={{ listStyle: 'none' }}>
      <button
        type="button"
        className="nl-search-result-chip"
        style={{
          ...styles.itemButton,
          animationDelay: `${index * 60}ms`,
          ...(approximate ? styles.itemApprox : {}),
          ...(hovered ? styles.itemHover : {}),
        }}
        onMouseDown={(e) => e.preventDefault()}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => onOpenFilters(item.groupId)}
        title={approximate
          ? 'Aproximación por banda. Abrir Filtros para ajustar.'
          : 'Abrir Filtros en esta sección'}
      >
        {content}
      </button>
    </li>
  );
}

const styles = {
  wrap: {
    padding: '10px 0 8px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
    padding: '0 14px 8px',
  },
  headerLabel: {
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.textPanelDescription,
  },
  mockBadge: {
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium,
    color: FILTER_ACCENT,
    backgroundColor: FILTER_ACCENT_14,
    borderRadius: '4px',
    padding: '2px 6px',
    flexShrink: 0,
  },
  note: {
    padding: '0 14px 8px',
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: '0 0 4px',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 14px',
  },
  itemButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    margin: 0,
    padding: '6px 14px',
    appearance: 'none',
    WebkitAppearance: 'none',
    border: 'none',
    borderLeft: '2px solid transparent',
    backgroundColor: 'transparent',
    color: colors.textPanelDescription,
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: typography.fontFamily.primary,
  },
  itemHover: {
    backgroundColor: FILTER_ACCENT_14,
    borderLeft: `2px solid ${FILTER_ACCENT}`,
  },
  itemApprox: {
    opacity: 0.78,
  },
  icon: {
    fontSize: '18px',
    color: FILTER_ACCENT,
    flexShrink: 0,
  },
  itemLabel: {
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.sm,
    color: colors.textPanelDescription,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  unresolved: {
    padding: '8px 14px 4px',
    borderTop: `1px solid ${colors.border}`,
  },
  unresolvedHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    marginBottom: '4px',
  },
  unresolvedTitle: {
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium,
    color: colors.textSecondary,
  },
  unresolvedList: {
    margin: 0,
    paddingLeft: '18px',
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    lineHeight: typography.lineHeight.normal,
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  adjustRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '0 14px 8px',
  },
  adjustLink: {
    margin: 0,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium,
    color: FILTER_ACCENT,
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '18px 14px',
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.sm,
    lineHeight: typography.lineHeight.normal,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  dot: {
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    backgroundColor: FILTER_ACCENT,
    display: 'inline-block',
  },
  statusError: {
    padding: '16px 14px',
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.sm,
    lineHeight: typography.lineHeight.normal,
    color: colors.error,
    textAlign: 'center',
  },
  statusErrorAction: {
    marginTop: '8px',
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium,
    color: FILTER_ACCENT,
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },
};

export default ParsedSearchSummary;
